import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Empty,
  Input,
  List,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import React, { useContext, useMemo, useState } from 'react';
import {
  ArrowLeftOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  DatabaseOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  RobotOutlined,
  SafetyOutlined,
  SearchOutlined,
  SettingOutlined,
  SyncOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { PlaygroundContext } from './context/PlaygroundContext';
import { GET_AGENTS, GET_ALIAS_DETAILS } from './graphql/mutations';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

interface AgentDetails {
  id: string;
  name: string;
  description?: string;
  modelId?: string;
  foundationModel?: string;
  status?: string;
  agentStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  knowledgeBases?: any[];
  actionGroups?: any[];
  promptOverrideConfiguration?: any;
  idleSessionTTLInSeconds?: number;
  agentVersion?: string;
  capabilities?: string[];
  agentInstructions?: string;
  aliases?: {
    id: string;
    name?: string;
    status?: string;
    description?: string;
  }[];
}

const AgentDefinitionsContent: React.FC = () => {
  const { setCurrentView } = useContext(PlaygroundContext);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [selectedAliasId, setSelectedAliasId] = useState<string>('');
  const [aliasDetails, setAliasDetails] = useState<any>(null);
  const [loadingAliasDetails, setLoadingAliasDetails] =
    useState<boolean>(false);
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: agentsData, loading: agentsLoading } = useQuery<{
    agentsList: AgentDetails[];
  }>(GET_AGENTS);

  const [getAliasDetailsMutation] = useMutation(GET_ALIAS_DETAILS, {
    onCompleted: (data) => {
      setAliasDetails(data.getAliasDetails);
      setLoadingAliasDetails(false);
      setAliasError(null);
    },
    onError: (error) => {
      console.error('Error loading alias details:', error);
      setLoadingAliasDetails(false);
      setAliasError('Failed to load alias details');
    }
  });

  // Agents are loaded via useQuery

  const [statusFilter, setStatusFilter] = useState<
    'all' | 'prepared' | 'not_prepared'
  >('all');

  // Helper: determine prepared status
  const isPreparedStatus = (status?: string) => {
    const s = status?.toUpperCase();
    return s === 'PREPARED' || s === 'ACTIVE' || s === 'READY';
  };

  // Filter agents based on search query and status filter
  const filteredAgents = useMemo(() => {
    let list = agentsData?.agentsList ?? [];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = list.filter(
        (agent) =>
          agent.name?.toLowerCase().includes(query) ||
          agent.id?.toLowerCase().includes(query) ||
          agent.description?.toLowerCase().includes(query) ||
          agent.foundationModel?.toLowerCase().includes(query) ||
          agent.capabilities?.some((cap) => cap.toLowerCase().includes(query))
      );
    }

    if (statusFilter !== 'all') {
      list = list.filter((agent) => {
        const st = agent.status || agent.agentStatus;
        const prepared = isPreparedStatus(st);
        return statusFilter === 'prepared' ? prepared : !prepared;
      });
    }

    return list;
  }, [agentsData?.agentsList, searchQuery, statusFilter]);

  const selectedAgent = agentsData?.agentsList?.find(
    (agent) => agent.id === selectedAgentId
  );

  const handleAliasSelect = async (aliasId: string) => {
    setSelectedAliasId(aliasId);
    setAliasDetails(null);
    setAliasError(null);

    if (!selectedAgent) return;

    const selectedAlias = selectedAgent.aliases?.find(
      (a: any) => a.id === aliasId
    );
    const aliasName = selectedAlias?.name || aliasId;

    setLoadingAliasDetails(true);
    await getAliasDetailsMutation({
      variables: {
        agentName: selectedAgent.name,
        aliasName
      }
    });
  };

  const getStatusIcon = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'READY':
      case 'PREPARED':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'UPDATING':
      case 'CREATING':
        return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      case 'FAILED':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getModelIcon = (modelId?: string) => {
    if (!modelId) return <CodeOutlined />;
    if (modelId.includes('claude'))
      return <RobotOutlined style={{ color: '#9254de' }} />;
    if (modelId.includes('gpt'))
      return <RobotOutlined style={{ color: '#13c2c2' }} />;
    if (modelId.includes('llama'))
      return <RobotOutlined style={{ color: '#fa8c16' }} />;
    return <RobotOutlined style={{ color: '#1890ff' }} />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getAgentPrompt = (config?: any) => {
    if (!config) {
      return null;
    }

    // Try different possible paths for the prompt text in AWS Bedrock agent configuration
    if (config.promptConfigurations) {
      for (const promptConfig of config.promptConfigurations) {
        if (promptConfig.basePromptTemplate) {
          return promptConfig.basePromptTemplate;
        }
        if (promptConfig.promptTemplateConfiguration?.text) {
          return promptConfig.promptTemplateConfiguration.text;
        }
        if (promptConfig.promptTemplate) {
          return promptConfig.promptTemplate;
        }
      }
    }

    // Fallback: check if the config itself contains prompt text
    if (config.basePromptTemplate) {
      return config.basePromptTemplate;
    }
    if (config.promptTemplate) {
      return config.promptTemplate;
    }
    if (config.text) {
      return config.text;
    }

    return null;
  };

  const getInferenceParams = (config?: any) => {
    if (!config?.promptConfigurations?.[0]?.inferenceConfiguration) {
      return { temperature: 'Default', maxTokens: 'Default', topP: 'Default' };
    }
    const params = config.promptConfigurations[0].inferenceConfiguration;
    return {
      temperature: params.temperature || 'Default',
      maxTokens: params.maximumLength || 'Default',
      topP: params.topP || 'Default'
    };
  };

  return (
    <div style={{ padding: '24px' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => setCurrentView('main')}
        style={{
          marginBottom: '24px',
          borderRadius: 20,
          border: 'none',
          background: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '6px 20px',
          height: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          transition: 'all 0.3s ease',
          fontSize: 14,
          fontWeight: 500
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        Back to Main Menu
      </Button>

      <Card
        title={
          <Space>
            <RobotOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0 }}>
              Agent Definitions
            </Title>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong style={{ marginBottom: '8px', display: 'block' }}>
              Search and Select an Agent
            </Text>

            {/* Search and Filter */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <Search
                placeholder="Search agents by name, ID, description, or capabilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                prefix={<SearchOutlined />}
                style={{ flex: 1 }}
                disabled={agentsLoading}
              />
              <Segmented
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Prepared', value: 'prepared' },
                  { label: 'Not prepared', value: 'not_prepared' }
                ]}
                value={statusFilter}
                onChange={(val) =>
                  setStatusFilter(val as 'all' | 'prepared' | 'not_prepared')
                }
                disabled={agentsLoading}
              />
            </div>

            {/* Agents List */}
            <Card
              size="small"
              bodyStyle={{ padding: 0 }}
              style={{ borderRadius: 8 }}
            >
              <Spin spinning={agentsLoading}>
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  <List
                    dataSource={filteredAgents}
                    locale={{
                      emptyText: searchQuery
                        ? `No agents found matching "${searchQuery}"`
                        : 'No agents available'
                    }}
                    renderItem={(agent) => {
                      const isSelected = agent.id === selectedAgentId;
                      return (
                        <List.Item
                          style={{
                            padding: '10px 12px',
                            cursor: agentsLoading ? 'not-allowed' : 'pointer',
                            background: isSelected ? '#e6f7ff' : 'transparent'
                          }}
                          onClick={() => {
                            if (agentsLoading) return;
                            setSelectedAgentId(agent.id);
                            setSelectedAliasId('');
                            setAliasDetails(null);
                            setAliasError(null);
                            setLoadingAliasDetails(false);
                          }}
                        >
                          <Space>
                            {getStatusIcon(agent.status || agent.agentStatus)}
                            <span>{agent.name}</span>
                            {!!agent.foundationModel && (
                              <Tag color="blue">{agent.foundationModel}</Tag>
                            )}
                          </Space>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              </Spin>
            </Card>

            {/* Search Results Info */}
            {searchQuery.trim() && (
              <div style={{ marginTop: 8, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Showing {filteredAgents?.length || 0} of{' '}
                  {agentsData?.agentsList?.length || 0} agents
                </Text>
              </div>
            )}
          </div>
        </Space>
      </Card>

      {selectedAgent ? (
        <>
          {/* Alias Selection Card */}
          <Card style={{ marginBottom: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Title level={5} style={{ margin: 0 }}>
                🎯 Select Agent Alias
              </Title>
              {selectedAgent.aliases && selectedAgent.aliases.length > 0 ? (
                <Select
                  style={{ width: '100%' }}
                  placeholder="Choose an alias to view details..."
                  value={selectedAliasId}
                  onChange={handleAliasSelect}
                  size="middle"
                  loading={loadingAliasDetails}
                >
                  {selectedAgent.aliases.map((alias: any) => (
                    <Option key={alias.id} value={alias.id}>
                      <Space>
                        <span>{alias.name}</span>
                        {!!alias.status && (
                          <Tag color="blue">{alias.status}</Tag>
                        )}
                      </Space>
                    </Option>
                  ))}
                </Select>
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="This agent has no aliases"
                  description="Please contact an administrator to set up aliases for this agent."
                />
              )}
              {!!aliasError && (
                <Alert type="error" showIcon message={aliasError} />
              )}
            </Space>
          </Card>

          {/* Loading state for alias details */}
          {!!loadingAliasDetails && (
            <Card
              style={{ textAlign: 'center', padding: 24, marginBottom: 24 }}
            >
              <Spin />
            </Card>
          )}

          {/* Details - Only after alias selected and details available */}
          {!!selectedAliasId && !!aliasDetails && (
            <Card
              title={
                <Space size="large">
                  {getModelIcon(
                    aliasDetails.foundationModel ||
                      selectedAgent.foundationModel
                  )}
                  <Title level={4} style={{ margin: 0 }}>
                    {selectedAgent.name}
                  </Title>
                  <Badge
                    status={
                      selectedAgent.status === 'ACTIVE' ||
                      selectedAgent.status === 'PREPARED'
                        ? 'success'
                        : 'processing'
                    }
                    text={
                      selectedAgent.status ||
                      selectedAgent.agentStatus ||
                      'Unknown'
                    }
                  />
                </Space>
              }
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
            >
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="large"
              >
                {/* Agent Description - show only if present */}
                {!!selectedAgent.description && (
                  <div>
                    <Title level={5}>
                      <InfoCircleOutlined /> Agent Description
                    </Title>
                    <div style={{ marginLeft: '24px' }}>
                      <Paragraph>{selectedAgent.description}</Paragraph>
                    </div>
                  </div>
                )}

                {/* Agent Instructions - prefer alias details (collapsible) */}
                <div>
                  <Collapse
                    bordered={false}
                    defaultActiveKey={[]}
                    expandIconPosition="start"
                    style={{ background: 'transparent' }}
                  >
                    <Collapse.Panel
                      header={
                        <Space>
                          <SettingOutlined />
                          <Text strong>Instructions</Text>
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            (click to expand/collapse)
                          </Text>
                        </Space>
                      }
                      key="instructions"
                    >
                      {aliasDetails.agentInstructions ? (
                        <Card
                          size="small"
                          style={{ backgroundColor: '#f5f5f5' }}
                          bodyStyle={{ padding: 12 }}
                        >
                          <div
                            style={{
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              fontSize: '14px',
                              lineHeight: '1.5',
                              maxHeight: 320,
                              overflowY: 'auto',
                              paddingRight: 8
                            }}
                          >
                            {aliasDetails.agentInstructions}
                          </div>
                        </Card>
                      ) : (
                        <Text type="secondary" italic>
                          No instructions available for this alias.
                        </Text>
                      )}
                    </Collapse.Panel>
                  </Collapse>
                </div>

                {/* Inference Parameters - only if real config present */}
                {!!selectedAgent.promptOverrideConfiguration && (
                  <>
                    <Divider />
                    <div>
                      <Title level={5}>
                        <ThunderboltOutlined /> Inference Parameters
                      </Title>
                      <Row gutter={[16, 16]} style={{ marginLeft: '24px' }}>
                        {(() => {
                          const params = getInferenceParams(
                            selectedAgent.promptOverrideConfiguration
                          );
                          return (
                            <>
                              <Col span={8}>
                                <Card size="small">
                                  <Space
                                    direction="vertical"
                                    align="center"
                                    style={{ width: '100%' }}
                                  >
                                    <Text type="secondary">Temperature</Text>
                                    <Text strong style={{ fontSize: '18px' }}>
                                      {params.temperature}
                                    </Text>
                                  </Space>
                                </Card>
                              </Col>
                              <Col span={8}>
                                <Card size="small">
                                  <Space
                                    direction="vertical"
                                    align="center"
                                    style={{ width: '100%' }}
                                  >
                                    <Text type="secondary">Max Tokens</Text>
                                    <Text strong style={{ fontSize: '18px' }}>
                                      {params.maxTokens}
                                    </Text>
                                  </Space>
                                </Card>
                              </Col>
                              <Col span={8}>
                                <Card size="small">
                                  <Space
                                    direction="vertical"
                                    align="center"
                                    style={{ width: '100%' }}
                                  >
                                    <Text type="secondary">Top P</Text>
                                    <Text strong style={{ fontSize: '18px' }}>
                                      {params.topP}
                                    </Text>
                                  </Space>
                                </Card>
                              </Col>
                            </>
                          );
                        })()}
                      </Row>
                    </div>
                  </>
                )}

                {/* Guardrails - always show section, indicate if missing */}
                <>
                  <Divider />
                  <div>
                    <Title level={5}>
                      <SafetyOutlined /> Guardrails
                    </Title>
                    <div style={{ marginLeft: '24px' }}>
                      {(() => {
                        const cfg = aliasDetails?.guardrailConfiguration;
                        const hasId =
                          !!cfg?.guardrailIdentifier &&
                          cfg.guardrailIdentifier.trim().length > 0;
                        const hasVersion =
                          !!cfg?.guardrailVersion &&
                          cfg.guardrailVersion.toString().trim().length > 0;
                        const hasGuardrails = hasId || hasVersion;

                        return hasGuardrails ? (
                          <Space direction="vertical" size={4}>
                            <Space size={8}>
                              <span role="img" aria-label="guardrails-on">
                                ✅
                              </span>
                              <Text strong>Guardrails enabled</Text>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              ID:{' '}
                              <Text code>
                                {hasId ? cfg!.guardrailIdentifier : 'Unknown'}
                              </Text>{' '}
                              • Version:{' '}
                              <Text code>
                                {hasVersion ? cfg!.guardrailVersion : 'Unknown'}
                              </Text>
                            </Text>
                          </Space>
                        ) : (
                          <Space size={8}>
                            <span role="img" aria-label="guardrails-off">
                              ❌
                            </span>
                            <Text type="secondary" italic>
                              No guardrails configured for this alias.
                            </Text>
                          </Space>
                        );
                      })()}
                    </div>
                  </div>
                </>

                <Divider />

                {/* Additional Details */}
                <Descriptions
                  title="Additional Information"
                  bordered
                  column={2}
                >
                  <Descriptions.Item label="Agent ID" span={2}>
                    <Text code>{selectedAgent.id}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Alias Name">
                    {aliasDetails.aliasName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Alias ID">
                    {aliasDetails.aliasId}
                  </Descriptions.Item>
                  <Descriptions.Item label="Foundation Model">
                    <Space>
                      {getModelIcon(aliasDetails.foundationModel)}
                      <Text>
                        {aliasDetails.foundationModel || 'Not specified'}
                      </Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Version">
                    {aliasDetails.agentVersion || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    <Space>
                      <ClockCircleOutlined />
                      {formatDate(aliasDetails.createdAt)}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Updated">
                    <Space>
                      <ClockCircleOutlined />
                      {formatDate(aliasDetails.updatedAt)}
                    </Space>
                  </Descriptions.Item>
                  {!!selectedAgent.idleSessionTTLInSeconds && (
                    <Descriptions.Item label="Session Timeout" span={2}>
                      {selectedAgent.idleSessionTTLInSeconds} seconds
                    </Descriptions.Item>
                  )}
                  {!!selectedAgent.capabilities &&
                    selectedAgent.capabilities.length > 0 && (
                      <Descriptions.Item label="Capabilities" span={2}>
                        <div>
                          {selectedAgent.capabilities.map(
                            (capability, index) => (
                              <Tag
                                key={index}
                                style={{
                                  margin: '2px 4px 2px 0',
                                  borderRadius: 6
                                }}
                              >
                                {capability}
                              </Tag>
                            )
                          )}
                        </div>
                      </Descriptions.Item>
                    )}
                </Descriptions>
              </Space>
            </Card>
          )}
        </>
      ) : (
        <Card style={{ textAlign: 'center', padding: '48px' }}>
          <Empty
            image={
              <RobotOutlined style={{ fontSize: '64px', color: '#bfbfbf' }} />
            }
            description={
              <Space direction="vertical">
                <Text type="secondary">No agent selected</Text>
                <Text type="secondary">
                  Choose an agent from the dropdown above to view its
                  configuration
                </Text>
              </Space>
            }
          />
        </Card>
      )}
    </div>
  );
};

export const AgentDefinitions: React.FC = () => {
  return <AgentDefinitionsContent />;
};

export default AgentDefinitions;
